/**
 * PWA helpers (plan-30).
 *
 * `isStandalonePwa` detects an installed-app context — Android/desktop
 * `display-mode: standalone` plus iOS Safari's legacy `navigator.standalone`.
 * Used to gate tablet-only behavior (orientation lock below; print gating
 * in T10 reuses it).
 */
export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/**
 * Best-effort landscape pin for the installed demo PWA. The manifest already
 * declares `"orientation": "landscape"`, but that field is captured at install
 * time — this covers installs made before it existed and OEM builds that
 * ignore the manifest. No-op outside installed-PWA contexts (a browser tab
 * cannot lock orientation without fullscreen anyway).
 */
export async function lockLandscapeOrientation(): Promise<void> {
  if (!isStandalonePwa()) return
  try {
    await (
      screen.orientation as ScreenOrientation & {
        lock?: (o: string) => Promise<void>
      }
    ).lock?.('landscape')
  } catch (err) {
    // Chrome/Android WebAPKs reject with SecurityError (fullscreen required)
    // — expected; the manifest "orientation" member baked at install time is
    // the real lock. Debug-log so chrome://inspect shows why nothing locked.
    console.debug('[pwa] orientation lock unavailable:', err)
  }
}
